import React, { useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import ChartCard from './components/ChartCard'
import Section from './components/Section'
import StatTile from './components/StatTile'
import { AXIS_TICK, CHART_COLORS, TOOLTIP_STYLE, formatPeriodStart, formatStatValue, hasNonZeroValue } from './constants'
import { OPERATION_PRODUCT_ANALYTICS_CODES } from './operations-gql/queries'
import { buildGridSx } from './styles'
import { CodesQuery, ProductAnalyticsPeriod, ProductAnalyticsPlatform, ProductAnalyticsVariables } from './types'

type CodesSectionProps = {
  period: ProductAnalyticsPeriod
  platform: ProductAnalyticsPlatform
}

const CodesSection: React.FC<CodesSectionProps> = ({ period, platform }) => {
  const { t, i18n } = useTranslation()
  const { data, loading, error } = useQuery<CodesQuery, ProductAnalyticsVariables>(
    OPERATION_PRODUCT_ANALYTICS_CODES,
    { variables: { period, platform }, errorPolicy: 'all' }
  )
  const codes = data?.productAnalytics

  const channels = useMemo(() => (
    codes?.codeDistribution?.map(row => ({
      ...row,
      label: t(`stats.product_analytics.codes.channels.${row.channel}`),
    })) ?? []
  ), [codes?.codeDistribution, t])

  const codeTypes = useMemo(() => (
    codes?.codeTypes?.map(row => ({
      ...row,
      label: t(`stats.product_analytics.codes.events.${row.event}`),
    })) ?? []
  ), [codes?.codeTypes, t])

  const formatOptionValue = (value: number | null | undefined, suffix = ''): string => (
    formatStatValue(value, i18n.language, suffix)
  )

  const hasFieldError = (field: unknown) => Boolean(error) && !field

  return (
    <Section
      id="product-analytics-codes"
      title={t('stats.product_analytics.codes.title')}
      columns={{ xs: 1, md: 2, lg: 4 }}
    >
      <ChartCard
        title={t('stats.product_analytics.codes.distribution')}
        description={t('stats.product_analytics.codes.distribution_description')}
        loading={loading}
        hasError={hasFieldError(codes?.codeDistribution)}
        isEmpty={!hasNonZeroValue(codes?.codeDistribution, ['actions'])}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={channels}>
            <CartesianGrid vertical={false} stroke="#F3F4F6" />
            <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} />
            <YAxis allowDecimals={false} tick={AXIS_TICK} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: '#F9FAFB' }} />
            <Bar dataKey="actions" name={t('stats.product_analytics.codes.actions')} fill={CHART_COLORS.primary} radius={[6, 6, 0, 0]} barSize={40} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard
        title={t('stats.product_analytics.codes.options')}
        description={t('stats.product_analytics.codes.options_description')}
        loading={loading}
        hasError={hasFieldError(codes?.codeOptions)}
        isEmpty={!codes?.codeOptions?.creations}
        bodyHeight="auto"
      >
        <Box sx={buildGridSx({ xs: 2 }, 1)}>
          <StatTile
            label={t('stats.product_analytics.codes.total_creations')}
            value={formatOptionValue(codes?.codeOptions?.creations)}
          />
          <StatTile
            label={t('stats.product_analytics.codes.with_offer')}
            value={formatOptionValue(codes?.codeOptions?.withOfferRate, ' %')}
          />
          <StatTile
            label={t('stats.product_analytics.codes.with_private_wifi')}
            value={formatOptionValue(codes?.codeOptions?.withPrivateWifiRate, ' %')}
          />
          <StatTile
            label={t('stats.product_analytics.codes.average_simultaneous_connections')}
            value={formatOptionValue(codes?.codeOptions?.averageSimultaneousConnections)}
          />
        </Box>
      </ChartCard>

      <ChartCard
        title={t('stats.product_analytics.codes.code_types')}
        description={t('stats.product_analytics.codes.code_types_description')}
        loading={loading}
        hasError={hasFieldError(codes?.codeTypes)}
        isEmpty={!hasNonZeroValue(codes?.codeTypes, ['standard', 'h3'])}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={codeTypes}>
            <CartesianGrid vertical={false} stroke="#F3F4F6" />
            <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} />
            <YAxis allowDecimals={false} tick={AXIS_TICK} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: '#F9FAFB' }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="standard" name={t('stats.product_analytics.codes.standard')} fill={CHART_COLORS.primary} radius={[6, 6, 0, 0]} />
            <Bar dataKey="h3" name={t('stats.product_analytics.codes.h3')} fill={CHART_COLORS.quinary} radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard
        title={t('stats.product_analytics.codes.disabling')}
        description={t('stats.product_analytics.codes.disabling_description')}
        loading={loading}
        hasError={hasFieldError(codes?.codeDisabling)}
        isEmpty={!hasNonZeroValue(codes?.codeDisabling, ['disabledCodes', 'generatedCodes'])}
      >
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={codes?.codeDisabling ?? []}>
            <CartesianGrid vertical={false} stroke="#F3F4F6" />
            <XAxis
              dataKey="periodStart"
              tickFormatter={(value: string) => formatPeriodStart(value, i18n.language)}
              tick={AXIS_TICK}
              tickLine={false}
            />
            <YAxis allowDecimals={false} tick={AXIS_TICK} tickLine={false} axisLine={false} />
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
              labelFormatter={(value: string) => formatPeriodStart(value, i18n.language)}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line
              type="monotone"
              dataKey="generatedCodes"
              name={t('stats.product_analytics.codes.generated_codes')}
              stroke={CHART_COLORS.primary}
              strokeWidth={2}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="disabledCodes"
              name={t('stats.product_analytics.codes.disabled_codes')}
              stroke={CHART_COLORS.tertiary}
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>
    </Section>
  )
}

export default CodesSection
